import { abortableDelay, throwIfAborted } from '../../lib/abortSignal';
import { parseRetryAfterMs } from '../../lib/httpRetry';
import { fetchWithTimeoutCircuit } from './pseFetchCircuit';

/** GDELT's live Doc API asks for at most one request every five seconds.
 * Both sentiment adapters share this process-local queue. An upstream 429
 * stops queued requests during the cooldown instead of multiplying failures.
 * Other workers may still exhaust the upstream quota; that remains a gap.
 */
export function createGdeltRequestGate(options: {
  fetcher?: (url: string, timeoutMs: number, signal?: AbortSignal) => Promise<Response>;
  now?: () => number;
  delay?: (ms: number, signal?: AbortSignal) => Promise<void>;
} = {}): (url: string, timeoutMs: number, signal?: AbortSignal) => Promise<Response> {
  const fetcher = options.fetcher ?? ((url, timeout, signal) => fetchWithTimeoutCircuit('gdelt', url, timeout, signal));
  const now = options.now ?? Date.now;
  const delay = options.delay ?? abortableDelay;
  let nextStart = 0;
  let cooldownUntil = 0;
  let tail = Promise.resolve();
  return (url, timeoutMs, signal) => {
    const request = tail.then(async () => {
      throwIfAborted(signal);
      if (now() < cooldownUntil) throw new Error('gdelt: HTTP 429 cooldown; source rate limit prevents this query.');
      const wait = nextStart - now();
      if (wait > 0) await delay(wait, signal);
      throwIfAborted(signal);
      nextStart = now() + 5_500;
      const response = await fetcher(url, timeoutMs, signal);
      if (response.status === 429) {
        cooldownUntil = now() + Math.max(60_000, parseRetryAfterMs(response.headers.get('Retry-After')) ?? 0);
        throw new Error('gdelt: HTTP 429 rate limited; remaining queries are paused during the source cooldown.');
      }
      return response;
    });
    tail = request.then(() => undefined, () => undefined);
    return request;
  };
}

export const fetchGdeltDocument = createGdeltRequestGate();
